import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import app from "../../firebase";
import AdminShell from "./AdminShell";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

const db = getFirestore(app);
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  border: "1px solid #cbd5d0",
  borderRadius: 6,
  fontSize: 14
};
const primaryButtonStyle = {
  border: 0,
  borderRadius: 6,
  padding: "10px 16px",
  background: "#155c2b",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer"
};

export default function Department() {
  const [activeTab, setActiveTab] = useState("departments");
  const [departments, setDepartments] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingDepartment, setDeletingDepartment] = useState(false);
  const [deletingSchool, setDeletingSchool] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [departmentSchool, setDepartmentSchool] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [departmentPage, setDepartmentPage] = useState(1);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editDepartmentError, setEditDepartmentError] = useState("");
  const [editDepartmentName, setEditDepartmentName] = useState("");
  const [editDepartmentSchool, setEditDepartmentSchool] = useState("");
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [departmentDeleteError, setDepartmentDeleteError] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [schoolPage, setSchoolPage] = useState(1);
  const [editingSchool, setEditingSchool] = useState(null);
  const [editSchoolName, setEditSchoolName] = useState("");
  const [editSchoolError, setEditSchoolError] = useState("");
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const [schoolDeleteError, setSchoolDeleteError] = useState("");
  const departmentPageSize = 10;
  const departmentSaveInProgress = useRef(false);
  const departmentDeleteInProgress = useRef(false);
  const schoolSaveInProgress = useRef(false);
  const schoolDeleteInProgress = useRef(false);
  const schoolPageSize = 10;

  useEffect(() => {
    const unsubscribeDepartments = onSnapshot(collection(db, "departments"), snapshot => {
      setDepartments(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
      setLoading(false);
    }, err => {
      setError(err.message || "Failed to load departments.");
      setLoading(false);
    });
    const unsubscribeSchools = onSnapshot(collection(db, "schools"), snapshot => {
      setSchools(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
    }, err => setError(err.message || "Failed to load academic divisions."));

    return () => {
      unsubscribeDepartments();
      unsubscribeSchools();
    };
  }, []);

  const resetDepartmentForm = () => {
    setDepartmentName("");
    setDepartmentSchool("");
  };

  const saveDepartment = async (event, { id = null, name: inputName, school: inputSchool }) => {
    event.preventDefault();
    if (departmentSaveInProgress.current) return;
    setError("");
    setMessage("");
    setEditDepartmentError("");
    const name = inputName.trim();
    const school = inputSchool.trim();
    if (!name || !school) {
      const validationError = "Department name and academic division are required.";
      if (id) setEditDepartmentError(validationError);
      else setError(validationError);
      return;
    }

    departmentSaveInProgress.current = true;
    setSaving(true);
    try {
      const currentDepartments = await getDocs(collection(db, "departments"));
      const normalizedName = name.trim().toLowerCase();
      const hasDuplicate = currentDepartments.docs.some(item => {
        if (item.id === id) return false;
        const existingName = item.data()?.name;
        return typeof existingName === "string" && existingName.trim().toLowerCase() === normalizedName;
      });
      if (hasDuplicate) {
        const duplicateError = "A department with this name already exists. Department names must be unique across all academic divisions.";
        if (id) setEditDepartmentError(duplicateError);
        else setError(duplicateError);
        return;
      }

      if (id) {
        await updateDoc(doc(db, "departments", id), { name, school });
        setMessage("Department updated.");
        setEditingDepartment(null);
      } else {
        await addDoc(collection(db, "departments"), { name, school });
        setMessage("Department added.");
      }
      if (!id) resetDepartmentForm();
    } catch (err) {
      console.error("Failed to check or save department", err);
      const saveError = err.message || "Unable to verify department name uniqueness or save the department. Please try again.";
      if (id) setEditDepartmentError(saveError);
      else setError(saveError);
    } finally {
      departmentSaveInProgress.current = false;
      setSaving(false);
    }
  };

  const handleSaveDepartment = event => saveDepartment(event, {
    name: departmentName,
    school: departmentSchool,
  });

  const handleSaveDepartmentEdit = event => saveDepartment(event, {
    id: editingDepartment?.id,
    name: editDepartmentName,
    school: editDepartmentSchool,
  });

  const handleDeleteDepartment = async () => {
    if (!departmentToDelete || departmentDeleteInProgress.current) return;
    departmentDeleteInProgress.current = true;
    setError("");
    setMessage("");
    setDepartmentDeleteError("");
    setDeletingDepartment(true);
    try {
      await deleteDoc(doc(db, "departments", departmentToDelete.id));
      setMessage("Department deleted.");
      setDepartmentToDelete(null);
    } catch (err) {
      setDepartmentDeleteError(err.message || "Failed to delete department.");
    } finally {
      departmentDeleteInProgress.current = false;
      setDeletingDepartment(false);
    }
  };

  const commitOperations = async operations => {
    for (let start = 0; start < operations.length; start += 450) {
      const batch = writeBatch(db);
      operations.slice(start, start + 450).forEach(operation => operation(batch));
      await batch.commit();
    }
  };

  const saveSchool = async (event, { id = null, inputName }) => {
    event.preventDefault();
    if (schoolSaveInProgress.current) return;
    setError("");
    setMessage("");
    setEditSchoolError("");
    const name = inputName.trim();
    if (!name) {
      const validationError = "Academic division name is required.";
      if (id) setEditSchoolError(validationError);
      else setError(validationError);
      return;
    }
    if (name.includes("/")) {
      const validationError = "Academic division names cannot contain a slash.";
      if (id) setEditSchoolError(validationError);
      else setError(validationError);
      return;
    }

    schoolSaveInProgress.current = true;
    setSaving(true);
    try {
      const currentSchools = await getDocs(collection(db, "schools"));
      const hasDuplicate = currentSchools.docs.some(item => {
        if (item.id === id) return false;
        const existingName = item.data()?.name;
        return typeof existingName === "string" && existingName.trim().toLowerCase() === name.toLowerCase();
      });
      if (hasDuplicate) {
        const duplicateError = "That academic division already exists.";
        if (id) setEditSchoolError(duplicateError);
        else setError(duplicateError);
        return;
      }

      if (!id) {
        await setDoc(doc(db, "schools", name), { name });
        setMessage("Academic division added.");
      } else {
        const currentSchool = currentSchools.docs.find(item => item.id === id)?.data();
        const oldName = currentSchool?.name || "";
        const [departmentSnapshot, userSnapshot] = oldName && oldName !== name
          ? await Promise.all([
            getDocs(query(collection(db, "departments"), where("school", "==", oldName))),
            getDocs(query(collection(db, "users"), where("school", "==", oldName)))
          ])
          : [{ docs: [] }, { docs: [] }];

        const newSchoolRef = doc(db, "schools", name);
        const operations = [batch => batch.set(newSchoolRef, { name }, { merge: true })];
        if (id !== name) {
          operations.push(batch => batch.delete(doc(db, "schools", id)));
        }
        departmentSnapshot.docs.forEach(item => {
          operations.push(batch => batch.update(item.ref, { school: name }));
        });
        userSnapshot.docs.forEach(item => {
          operations.push(batch => batch.update(item.ref, { school: name }));
        });
        await commitOperations(operations);
        setMessage("Academic division updated.");
        setEditingSchool(null);
      }
      if (!id) setSchoolName("");
    } catch (err) {
      console.error("Failed to check or save academic division", err);
      const saveError = err.message || "Unable to verify academic division uniqueness or save it. Please try again.";
      if (id) setEditSchoolError(saveError);
      else setError(saveError);
    } finally {
      schoolSaveInProgress.current = false;
      setSaving(false);
    }
  };

  const handleSaveSchool = event => saveSchool(event, { inputName: schoolName });

  const handleSaveSchoolEdit = event => saveSchool(event, {
    id: editingSchool?.id,
    inputName: editSchoolName
  });

  const handleDeleteSchool = async () => {
    if (!schoolToDelete || schoolDeleteInProgress.current) return;
    schoolDeleteInProgress.current = true;
    setError("");
    setMessage("");
    setSchoolDeleteError("");
    setDeletingSchool(true);
    try {
      const [departmentSnapshot, userSnapshot] = await Promise.all([
        getDocs(query(collection(db, "departments"), where("school", "==", schoolToDelete.name))),
        getDocs(query(collection(db, "users"), where("school", "==", schoolToDelete.name)))
      ]);
      if (!departmentSnapshot.empty || !userSnapshot.empty) {
        setSchoolDeleteError("This academic division is assigned to departments or users. Reassign them before deleting it.");
        return;
      }
      await deleteDoc(doc(db, "schools", schoolToDelete.id));
      setMessage("Academic division deleted.");
      setSchoolToDelete(null);
    } catch (err) {
      setSchoolDeleteError(err.message || "Failed to delete academic division.");
    } finally {
      schoolDeleteInProgress.current = false;
      setDeletingSchool(false);
    }
  };

  const beginEditDepartment = department => {
    setEditingDepartment(department);
    setEditDepartmentName(department.name || "");
    setEditDepartmentSchool(department.school || "");
    setEditDepartmentError("");
    setActiveTab("departments");
    setError("");
    setMessage("");
  };

  const sortedDepartments = useMemo(
    () => [...departments].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [departments]
  );
  const filteredDepartments = useMemo(() => {
    const searchTerm = departmentSearch.trim().toLowerCase();
    return sortedDepartments.filter(department => !searchTerm || (department.name || "").toLowerCase().includes(searchTerm));
  }, [sortedDepartments, departmentSearch]);
  const departmentTotalPages = Math.max(1, Math.ceil(filteredDepartments.length / departmentPageSize));
  const pagedDepartments = filteredDepartments.slice(
    (departmentPage - 1) * departmentPageSize,
    departmentPage * departmentPageSize
  );

  useEffect(() => {
    setDepartmentPage(1);
  }, [departmentSearch]);

  useEffect(() => {
    setDepartmentPage(page => Math.min(page, departmentTotalPages));
  }, [departmentTotalPages]);

  const beginEditSchool = school => {
    setEditingSchool(school);
    setEditSchoolName(school.name || "");
    setEditSchoolError("");
    setActiveTab("schools");
    setError("");
    setMessage("");
  };

  const sortedSchools = useMemo(
    () => [...schools].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [schools]
  );
  const filteredSchools = useMemo(() => {
    const searchTerm = schoolSearch.trim().toLowerCase();
    return sortedSchools.filter(school => !searchTerm || (school.name || "").toLowerCase().includes(searchTerm));
  }, [sortedSchools, schoolSearch]);
  const schoolTotalPages = Math.max(1, Math.ceil(filteredSchools.length / schoolPageSize));
  const pagedSchools = filteredSchools.slice((schoolPage - 1) * schoolPageSize, schoolPage * schoolPageSize);

  useEffect(() => {
    setSchoolPage(1);
  }, [schoolSearch]);

  useEffect(() => {
    setSchoolPage(page => Math.min(page, schoolTotalPages));
  }, [schoolTotalPages]);

  return (
    <AdminShell title="Departments & Academic Divisions">
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: 20, flexWrap: "wrap", marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, color: "#183b2a", fontSize: 26 }}>Departments &amp; Academic Divisions</h1>
            <p style={{ margin: "8px 0 0", color: "#5c6861" }}>Maintain the academic divisions and the departments assigned to them.</p>
          </div>
          <div role="tablist" aria-label="Academic structure" style={{ display: "flex", border: "1px solid #cad5ce", borderRadius: 7, overflow: "hidden", background: "#fff" }}>
            {[{ key: "departments", label: "Departments" }, { key: "schools", label: "School of" }].map(tab => (
              <button key={tab.key} type="button" role="tab" aria-selected={activeTab === tab.key} onClick={() => { setActiveTab(tab.key); setError(""); setMessage(""); }} style={{ padding: "10px 16px", border: 0, background: activeTab === tab.key ? "#155c2b" : "#fff", color: activeTab === tab.key ? "#fff" : "#26352c", fontWeight: 700, cursor: "pointer" }}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {error && <div role="alert" style={{ marginBottom: 16, padding: 12, borderRadius: 6, background: "#fdecea", color: "#a4261b" }}>{error}</div>}
        {message && <div role="status" style={{ marginBottom: 16, padding: 12, borderRadius: 6, background: "#e8f4ec", color: "#155c2b" }}>{message}</div>}

        {activeTab === "departments" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 20, alignItems: "start" }}>
            <section style={{ background: "#fff", border: "1px solid #e0e7e2", borderRadius: 8, padding: 22 }}>
              <h2 style={{ margin: "0 0 18px", fontSize: 18, color: "#183b2a" }}>Add department</h2>
              <form onSubmit={handleSaveDepartment}>
                <label htmlFor="department-name" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>Department name</label>
                <input id="department-name" value={departmentName} onChange={event => setDepartmentName(event.target.value)} required maxLength={100} style={{ ...inputStyle, marginBottom: 14 }} />
                <label htmlFor="department-school" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>School of (academic division)</label>
                <select id="department-school" value={departmentSchool} onChange={event => setDepartmentSchool(event.target.value)} required style={{ ...inputStyle, marginBottom: 16 }}>
                  <option value="">Select an academic division</option>
                  {schools.map(school => <option key={school.id} value={school.name}>{school.name}</option>)}
                </select>
                {schools.length === 0 && <p style={{ margin: "-6px 0 14px", color: "#8a4b08", fontSize: 13 }}>Add a School of division before creating departments.</p>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="submit" disabled={saving || schools.length === 0} style={{ ...primaryButtonStyle, opacity: saving || schools.length === 0 ? 0.6 : 1 }}>{saving ? "Checking and saving..." : "Add department"}</button>
                </div>
              </form>
            </section>
            <section style={{ background: "#fff", border: "1px solid #e0e7e2", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "18px 20px", borderBottom: "1px solid #e6ebe8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: 18, color: "#183b2a" }}>Departments</h2>
                <span style={{ color: "#657169", fontSize: 13 }}>{filteredDepartments.length} of {departments.length} departments</span>
              </div>
              <div style={{ display: "flex", alignItems: "end", gap: 12, flexWrap: "wrap", padding: 14, borderBottom: "1px solid #e6ebe8" }}>
                <div style={{ flex: "1 1 220px" }}>
                  <label htmlFor="department-search" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>Filter by department name</label>
                  <select
                    id="department-search"
                    value={departmentSearch}
                    onChange={event => setDepartmentSearch(event.target.value)}
                    style={inputStyle}
                  >
                    <option value="">All Departments</option>
                    {[...new Set(sortedDepartments.map(department => (department.name || "").trim()).filter(Boolean))].map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setDepartmentSearch("")}
                  disabled={!departmentSearch}
                  style={{ padding: "10px 14px", border: "1px solid #cad5ce", borderRadius: 6, background: departmentSearch ? "#fff" : "#f4f7f5", color: "#26352c", fontWeight: 600, cursor: departmentSearch ? "pointer" : "not-allowed" }}
                >
                  Clear all filters
                </button>
              </div>
              {loading ? <p style={{ padding: 20, color: "#657169" }}>Loading departments...</p> : filteredDepartments.length === 0 ? (
                <p style={{ padding: 20, color: "#657169" }}>{departments.length === 0 ? "No departments have been added." : "No departments match your search."}</p>
              ) : (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead><tr style={{ background: "#f4f7f5", textAlign: "left" }}><th style={{ padding: 12 }}>Department</th><th style={{ padding: 12 }}>School of</th><th style={{ padding: 12 }}>Actions</th></tr></thead>
                      <tbody>{pagedDepartments.map(department => (
                        <tr key={department.id} style={{ borderTop: "1px solid #e8eeea" }}>
                          <td style={{ padding: 12 }}>{department.name || "Unnamed department"}</td>
                          <td style={{ padding: 12 }}>{department.school || "Unassigned"}</td>
                          <td style={{ padding: 12, whiteSpace: "nowrap" }}>
                            <button type="button" onClick={() => beginEditDepartment(department)} style={{ marginRight: 8, border: "1px solid #cad5ce", borderRadius: 5, background: "#fff", padding: "6px 9px", cursor: "pointer" }}>Edit</button>
                            <button type="button" onClick={() => { setDepartmentDeleteError(""); setDepartmentToDelete(department); }} style={{ border: "1px solid #e5c4c1", borderRadius: 5, background: "#fff", color: "#a4261b", padding: "6px 9px", cursor: "pointer" }}>Delete</button>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: 14 }}>
                    <span style={{ color: "#657169", fontSize: 13 }}>Page {departmentPage} of {departmentTotalPages}</span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" onClick={() => setDepartmentPage(page => Math.max(1, page - 1))} disabled={departmentPage === 1} style={{ padding: "7px 12px", border: "1px solid #cad5ce", borderRadius: 5, background: departmentPage === 1 ? "#f4f7f5" : "#fff", cursor: departmentPage === 1 ? "not-allowed" : "pointer" }}>Previous</button>
                      <button type="button" onClick={() => setDepartmentPage(page => Math.min(departmentTotalPages, page + 1))} disabled={departmentPage === departmentTotalPages} style={{ padding: "7px 12px", border: "1px solid #cad5ce", borderRadius: 5, background: departmentPage === departmentTotalPages ? "#f4f7f5" : "#fff", cursor: departmentPage === departmentTotalPages ? "not-allowed" : "pointer" }}>Next</button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 20, alignItems: "start" }}>
            <section style={{ background: "#fff", border: "1px solid #e0e7e2", borderRadius: 8, padding: 22 }}>
              <h2 style={{ margin: "0 0 18px", fontSize: 18, color: "#183b2a" }}>Add School of</h2>
              <form onSubmit={handleSaveSchool}>
                <label htmlFor="school-name" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>Academic division name</label>
                <input id="school-name" value={schoolName} onChange={event => setSchoolName(event.target.value)} required maxLength={100} style={{ ...inputStyle, marginBottom: 16 }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="submit" disabled={saving} style={{ ...primaryButtonStyle, opacity: saving ? 0.6 : 1 }}>{saving ? "Checking and saving..." : "Add School of"}</button>
                </div>
              </form>
            </section>
            <section style={{ background: "#fff", border: "1px solid #e0e7e2", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "18px 20px", borderBottom: "1px solid #e6ebe8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: 18, color: "#183b2a" }}>Schools of</h2>
                <span style={{ color: "#657169", fontSize: 13 }}>{filteredSchools.length} of {schools.length} academic divisions</span>
              </div>
              <div style={{ display: "flex", alignItems: "end", gap: 12, flexWrap: "wrap", padding: 14, borderBottom: "1px solid #e6ebe8" }}>
                <div style={{ flex: "1 1 220px" }}>
                  <label htmlFor="school-search" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>Filter by School of</label>
                  <select id="school-search" value={schoolSearch} onChange={event => setSchoolSearch(event.target.value)} style={inputStyle}>
                    <option value="">All Schools of</option>
                    {[...new Set(sortedSchools.map(school => (school.name || "").trim()).filter(Boolean))].map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                </div>
                <button type="button" onClick={() => setSchoolSearch("")} disabled={!schoolSearch} style={{ padding: "10px 14px", border: "1px solid #cad5ce", borderRadius: 6, background: schoolSearch ? "#fff" : "#f4f7f5", color: "#26352c", fontWeight: 600, cursor: schoolSearch ? "pointer" : "not-allowed" }}>Clear all filters</button>
              </div>
              {filteredSchools.length === 0 ? <p style={{ padding: 20, color: "#657169" }}>{schools.length === 0 ? "No academic divisions have been added." : "No academic divisions match your filter."}</p> : (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead><tr style={{ background: "#f4f7f5", textAlign: "left" }}><th style={{ padding: 12 }}>Academic division</th><th style={{ padding: 12 }}>Actions</th></tr></thead>
                      <tbody>{pagedSchools.map(school => (
                      <tr key={school.id} style={{ borderTop: "1px solid #e8eeea" }}>
                        <td style={{ padding: 12 }}>{school.name || "Unnamed division"}</td>
                        <td style={{ padding: 12, whiteSpace: "nowrap" }}>
                          <button type="button" onClick={() => beginEditSchool(school)} style={{ marginRight: 8, border: "1px solid #cad5ce", borderRadius: 5, background: "#fff", padding: "6px 9px", cursor: "pointer" }}>Edit</button>
                          <button type="button" onClick={() => { setSchoolDeleteError(""); setSchoolToDelete(school); }} style={{ border: "1px solid #e5c4c1", borderRadius: 5, background: "#fff", color: "#a4261b", padding: "6px 9px", cursor: "pointer" }}>Delete</button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: 14 }}>
                    <span style={{ color: "#657169", fontSize: 13 }}>Page {schoolPage} of {schoolTotalPages}</span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" onClick={() => setSchoolPage(page => Math.max(1, page - 1))} disabled={schoolPage === 1} style={{ padding: "7px 12px", border: "1px solid #cad5ce", borderRadius: 5, background: schoolPage === 1 ? "#f4f7f5" : "#fff", cursor: schoolPage === 1 ? "not-allowed" : "pointer" }}>Previous</button>
                      <button type="button" onClick={() => setSchoolPage(page => Math.min(schoolTotalPages, page + 1))} disabled={schoolPage === schoolTotalPages} style={{ padding: "7px 12px", border: "1px solid #cad5ce", borderRadius: 5, background: schoolPage === schoolTotalPages ? "#f4f7f5" : "#fff", cursor: schoolPage === schoolTotalPages ? "not-allowed" : "pointer" }}>Next</button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>
        )}
        {editingSchool && (
          <div role="presentation" style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15, 23, 18, 0.48)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <section role="dialog" aria-modal="true" aria-labelledby="edit-school-title" style={{ width: "100%", maxWidth: 520, background: "#fff", borderRadius: 10, padding: 24, boxShadow: "0 18px 48px rgba(0,0,0,0.22)" }}>
              <h2 id="edit-school-title" style={{ margin: "0 0 18px", fontSize: 20, color: "#183b2a" }}>Edit School of</h2>
              <form onSubmit={handleSaveSchoolEdit}>
                <label htmlFor="edit-school-name" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>Academic division name</label>
                <input id="edit-school-name" value={editSchoolName} onChange={event => setEditSchoolName(event.target.value)} required maxLength={100} style={{ ...inputStyle, marginBottom: 14 }} />
                {editSchoolError && <div role="alert" style={{ marginBottom: 14, padding: 10, borderRadius: 6, background: "#fdecea", color: "#a4261b" }}>{editSchoolError}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" disabled={saving} onClick={() => { setEditingSchool(null); setEditSchoolError(""); }} style={{ padding: "10px 14px", border: "1px solid #cbd5d0", borderRadius: 6, background: "#fff", cursor: saving ? "not-allowed" : "pointer" }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ ...primaryButtonStyle, opacity: saving ? 0.6 : 1 }}>{saving ? "Checking and saving..." : "Save changes"}</button>
                </div>
              </form>
            </section>
          </div>
        )}
        <DeleteConfirmationModal
          open={Boolean(schoolToDelete)}
          title="Delete School of?"
          subtitle="Confirm academic division deletion"
          name={schoolToDelete?.name || "this academic division"}
          message={`Are you sure you want to delete ${schoolToDelete?.name || "this academic division"}? This action cannot be undone.`}
          detail="It can only be deleted when no departments or users are assigned to it. Please verify the details before proceeding."
          error={schoolDeleteError}
          deleting={deletingSchool}
          confirmLabel="Delete School of"
          onCancel={() => { setSchoolToDelete(null); setSchoolDeleteError(""); }}
          onConfirm={handleDeleteSchool}
        />
        {editingDepartment && (
          <div role="presentation" style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15, 23, 18, 0.48)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <section role="dialog" aria-modal="true" aria-labelledby="edit-department-title" style={{ width: "100%", maxWidth: 520, background: "#fff", borderRadius: 10, padding: 24, boxShadow: "0 18px 48px rgba(0,0,0,0.22)" }}>
              <h2 id="edit-department-title" style={{ margin: "0 0 18px", fontSize: 20, color: "#183b2a" }}>Edit department</h2>
              <form onSubmit={handleSaveDepartmentEdit}>
                <label htmlFor="edit-department-name" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>Department name</label>
                <input id="edit-department-name" value={editDepartmentName} onChange={event => setEditDepartmentName(event.target.value)} required maxLength={100} style={{ ...inputStyle, marginBottom: 14 }} />
                <label htmlFor="edit-department-school" style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 700 }}>School of (academic division)</label>
                <select id="edit-department-school" value={editDepartmentSchool} onChange={event => setEditDepartmentSchool(event.target.value)} required style={{ ...inputStyle, marginBottom: 14 }}>
                  <option value="">Select an academic division</option>
                  {editDepartmentSchool && !schools.some(school => school.name === editDepartmentSchool) && <option value={editDepartmentSchool}>{editDepartmentSchool}</option>}
                  {schools.map(school => <option key={school.id} value={school.name}>{school.name}</option>)}
                </select>
                {editDepartmentError && <div role="alert" style={{ marginBottom: 14, padding: 10, borderRadius: 6, background: "#fdecea", color: "#a4261b" }}>{editDepartmentError}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" disabled={saving} onClick={() => { setEditingDepartment(null); setEditDepartmentError(""); }} style={{ padding: "10px 14px", border: "1px solid #cbd5d0", borderRadius: 6, background: "#fff", cursor: saving ? "not-allowed" : "pointer" }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ ...primaryButtonStyle, opacity: saving ? 0.6 : 1 }}>{saving ? "Checking and saving..." : "Save changes"}</button>
                </div>
              </form>
            </section>
          </div>
        )}
        <DeleteConfirmationModal
          open={Boolean(departmentToDelete)}
          title="Delete Department?"
          subtitle="Confirm department deletion"
          name={departmentToDelete?.name || "this department"}
          message={`Are you sure you want to delete ${departmentToDelete?.name || "this department"}? This action cannot be undone.`}
          detail={`This department is in ${departmentToDelete?.school || "an unassigned school"}. Existing records that refer to it may remain. Please verify the details before proceeding.`}
          error={departmentDeleteError}
          deleting={deletingDepartment}
          confirmLabel="Delete Department"
          onCancel={() => { setDepartmentToDelete(null); setDepartmentDeleteError(""); }}
          onConfirm={handleDeleteDepartment}
        />
      </div>
    </AdminShell>
  );
}